import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, RefreshCw, Loader2, Info } from 'lucide-react';
import { penaltyService } from '@/services/penalty.service';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';

function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getDaysAgoISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface Props {
  onClose: () => void;
}

export function BackfillPenaltiesModal({ onClose }: Props) {
  const qc = useQueryClient();

  const [dateFrom, setDateFrom] = useState(getDaysAgoISO(21));
  const [dateTo,   setDateTo]   = useState(getDaysAgoISO(1));
  const [error,    setError]    = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => penaltyService.generate({ date_from: dateFrom, date_to: dateTo }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['penalty'] });
      toast.success(`Backfill complete — ${result.generated} penalty record(s) generated for ${result.date_from} to ${result.date_to}`);
      onClose();
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to backfill penalties';
      setError(msg);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!dateFrom || !dateTo)      { setError('Select both a start and end date.'); return; }
    if (dateFrom > dateTo)         { setError('Start date must be before end date.'); return; }
    if (dateTo >= getTodayISO())   { setError('End date must be in the past.'); return; }
    mutation.mutate();
  }

  const submitting = mutation.isPending;

  return createPortal(
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-popover shadow-2xl">

        <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-border bg-popover px-5 py-4 rounded-t-2xl">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-500/10 border border-teal-500/20">
            <RefreshCw className="h-4 w-4 text-teal-600 dark:text-teal-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-foreground">Backfill Penalties</h2>
            <p className="text-xs text-muted-foreground">Re-check a date range for missed penalties</p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                From <span className="text-destructive">*</span>
              </label>
              <DatePicker value={dateFrom} onChange={setDateFrom} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                To <span className="text-destructive">*</span>
              </label>
              <DatePicker value={dateTo} onChange={setDateTo} />
            </div>
          </div>

          <div className="flex items-start gap-2.5 rounded-lg border border-sky-300/50 bg-sky-50 px-3.5 py-2.5 text-xs text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Re-checks every day in this range against raw punch logs and auto-generates any missing Miss Punch or Late Arrival penalties.
              Already-approved or rejected penalties are never touched.
              Dates still inside the configured waiting period are skipped. No late penalty is created
              when approved leave covers the arrival — full-day, morning half-day, or a short leave
              overlapping the time the employee was missing.
            </span>
          </div>

          {error && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={submitting}
              className="bg-teal-600 hover:bg-teal-700 text-white gap-1.5"
            >
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {submitting ? 'Running…' : 'Run Backfill'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
